"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, ArrowRight, SlidersHorizontal } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { JobListSkeleton } from "./skeletons";
import { useEnv } from "@/context/envContext";  

interface JobListSectionProps {
  cmsData: {
    jobs?: {
      content: {
        title: string;
        description: string;
        categories: string[];
      };
      style: {
        backgroundColor: string;
        textColor: string;
        fontSize: string;
        fontWeight: string;
      };
    };
  };
}

interface JobCategory {
  categoryId: string;
  name: string;
  description: string;
  typeOfRole: string[];
  isHighlighted: boolean;
  jobs: Array<{
    jobId: string;
    title: string;
    location: string;
    employmentType: string;
    shortDescription: string;
    department: string;
    experienceLevel: string;
    description: string;
    responsibilities: string[];
    requirements: string[];
    status: string;
    createdAt: string;
    updatedAt: string;
    isSpecialized: boolean;
    isDisplayed: boolean;
    headcount: number;
    categoryId: string;
    pipelineId: string;
    ownerId: string | null;
    // applications: any[];
  }>;
}

export default function JobListSection({ cmsData }: JobListSectionProps) {
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState<JobCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedEmploymentTypes, setSelectedEmploymentTypes] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [expandedFilterSections, setExpandedFilterSections] = useState<string[]>([
    'location', 'employment', 'department'
  ]);
  const env = useEnv();

  const content = cmsData.jobs?.content;
  const style = cmsData.jobs?.style;

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${env.NEXT_PUBLIC_EXTERNAL_API_URL}/api/career/jobs`
        );

        const { data } = await response.json();
        const sortedData = data
          .map((category: JobCategory) => ({
            ...category,
            jobs: [...category.jobs].sort(
              (a: { title: string }, b: { title: string }) =>
                a.title.localeCompare(b.title)
            ),
          }))
          .sort((a: JobCategory, b: JobCategory) =>
            a.name.localeCompare(b.name)
          );
        setCategories(sortedData);
      } catch (error) {
        console.error("Error fetching job categories:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    // Handle direct navigation to #job-list
    if (window.location.hash === '#job-list') {
      const element = document.getElementById('job-list');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  const toggleCategory = (id: string) => {
    setExpandedCategories((prev) =>
      prev.includes(id) ? prev.filter((catId) => catId !== id) : [...prev, id]
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (query) {
      const matchingCategories = categories
        .filter((category) =>
          category.jobs.some((job) => job.title.toLowerCase().includes(query))
        )
        .map((category) => category.categoryId);

      setExpandedCategories(matchingCategories);
    } else {
      setExpandedCategories([]);
    }
  };

  const toggleFilterSection = (section: string) => {
    setExpandedFilterSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );
  };

  const handleFilterChange = (
    filterType: 'location' | 'employment' | 'department',
    value: string
  ) => {
    const setters = {
      location: setSelectedLocations,
      employment: setSelectedEmploymentTypes,
      department: setSelectedDepartments,
    };

    const setter = setters[filterType];
    setter((prev: string[]) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  // Extract unique values for filters
  const allJobs = categories.flatMap((cat) => cat.jobs);
  const uniqueLocations = Array.from(new Set(allJobs.map((job) => job.location).filter(Boolean)));
  const uniqueEmploymentTypes = Array.from(new Set(allJobs.map((job) => job.employmentType).filter(Boolean)));
  const uniqueDepartments = Array.from(new Set(allJobs.map((job) => job.department).filter(Boolean)));

  // Check if any filters are active
  const hasActiveFilters = 
    searchQuery ||
    selectedLocations.length > 0 ||
    selectedEmploymentTypes.length > 0 ||
    selectedDepartments.length > 0;

  // Apply filters
  const filteredCategories = categories
    .map((category) => ({
      ...category,
      jobs: category.jobs.filter((job) => {
        const matchesSearch = job.title.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesLocation = selectedLocations.length === 0 || selectedLocations.includes(job.location);
        const matchesEmployment = selectedEmploymentTypes.length === 0 || selectedEmploymentTypes.includes(job.employmentType);
        const matchesDepartment = selectedDepartments.length === 0 || selectedDepartments.includes(job.department);
        
        return matchesSearch && matchesLocation && matchesEmployment && matchesDepartment;
      }),
    }))
    .filter((category) => category.jobs.length > 0);

  const resetAllFilters = () => {
    setSearchQuery("");
    setSelectedLocations([]);
    setSelectedEmploymentTypes([]);
    setSelectedDepartments([]);
    setExpandedCategories([]);
  };

  if (isLoading) {
    return (
      <section id="job-list" className="py-8 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <JobListSkeleton />
        </div>
      </section>
    );
  }

  return (
    <section
      data-scroll-section
      id="job-list"
      className="min-h-screen py-8 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: "#f7f9fc" }}
    >
      <div className="max-w-7xl mx-auto w-full">
        <h2
          className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 lg:mb-8"
          style={{
            color: style?.textColor || "#164a7a",
          }}
        >
          {content?.title || "Careers at DANA"}
        </h2>

        {/* Search Bar with Toggle Filter Button */}
        {categories.length > 0 && (
          <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors whitespace-nowrap"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {showFilters ? 'Hide filter' : 'Show filter'}
            </button>
            <Input
              type="text"
              placeholder="Find the right role for you"
              value={searchQuery}
              onChange={handleSearch}
              className="flex-1 max-w-full sm:max-w-md text-black"
              style={{ backgroundColor: "#ffffff" }}
            />
          </div>
        )}

        {/* Job Count */}
        {categories.length > 0 && (
          <p className="text-sm text-gray-600 mb-4">
            {filteredCategories.reduce((acc, cat) => acc + cat.jobs.length, 0)} jobs available
          </p>
        )}

        {/* Mobile Filters */}
        {showFilters && categories.length > 0 && (
          <div className="lg:hidden mb-6">
            <Card className="p-4">
              <div className="space-y-4">
                  {/* Department Filter */}
                  {uniqueDepartments.length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleFilterSection('department')}
                        className="flex items-center justify-between w-full mb-3"
                      >
                        <h3 className="font-medium text-gray-900">Department</h3>
                        {expandedFilterSections.includes('department') ? (
                          <ChevronUp className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        )}
                      </button>
                      {expandedFilterSections.includes('department') && (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {uniqueDepartments.sort().map((dept) => (
                            <label
                              key={dept}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                            >
                              <Checkbox
                                checked={selectedDepartments.includes(dept)}
                                onCheckedChange={() => handleFilterChange('department', dept)}
                              />
                              <span className="text-sm text-gray-700">{dept}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Employment Type Filter */}
                  {uniqueEmploymentTypes.length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleFilterSection('employment')}
                        className="flex items-center justify-between w-full mb-3"
                      >
                        <h3 className="font-medium text-gray-900">Employment type</h3>
                        {expandedFilterSections.includes('employment') ? (
                          <ChevronUp className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        )}
                      </button>
                      {expandedFilterSections.includes('employment') && (
                        <div className="space-y-2">
                          {uniqueEmploymentTypes.sort().map((type) => (
                            <label
                              key={type}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                            >
                              <Checkbox
                                checked={selectedEmploymentTypes.includes(type)}
                                onCheckedChange={() => handleFilterChange('employment', type)}
                              />
                              <span className="text-sm text-gray-700">
                                {type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Location Filter */}
                  {uniqueLocations.length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleFilterSection('location')}
                        className="flex items-center justify-between w-full mb-3"
                      >
                        <h3 className="font-medium text-gray-900">Location</h3>
                        {expandedFilterSections.includes('location') ? (
                          <ChevronUp className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        )}
                      </button>
                      {expandedFilterSections.includes('location') && (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {uniqueLocations.sort().map((location) => (
                            <label
                              key={location}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                            >
                              <Checkbox
                                checked={selectedLocations.includes(location)}
                                onCheckedChange={() => handleFilterChange('location', location)}
                              />
                              <span className="text-sm text-gray-700">{location}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}

        {/* Main Content Grid - Desktop */}
        <div className="flex gap-6">
          {/* Desktop Filter Sidebar */}
          {showFilters && categories.length > 0 && (
            <div className="hidden lg:block w-64 flex-shrink-0">
              <Card className="p-4 sticky top-4">
                <div className="space-y-6">
                  {/* Department Filter */}
                  {uniqueDepartments.length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleFilterSection('department')}
                        className="flex items-center justify-between w-full mb-3"
                      >
                        <h3 className="font-medium text-gray-900">Department</h3>
                        {expandedFilterSections.includes('department') ? (
                          <ChevronUp className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        )}
                      </button>
                      {expandedFilterSections.includes('department') && (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {uniqueDepartments.sort().map((dept) => (
                            <label
                              key={dept}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                            >
                              <Checkbox
                                checked={selectedDepartments.includes(dept)}
                                onCheckedChange={() => handleFilterChange('department', dept)}
                              />
                              <span className="text-sm text-gray-700">{dept}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Employment Type Filter */}
                  {uniqueEmploymentTypes.length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleFilterSection('employment')}
                        className="flex items-center justify-between w-full mb-3"
                      >
                        <h3 className="font-medium text-gray-900">Employment type</h3>
                        {expandedFilterSections.includes('employment') ? (
                          <ChevronUp className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        )}
                      </button>
                      {expandedFilterSections.includes('employment') && (
                        <div className="space-y-2">
                          {uniqueEmploymentTypes.sort().map((type) => (
                            <label
                              key={type}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                            >
                              <Checkbox
                                checked={selectedEmploymentTypes.includes(type)}
                                onCheckedChange={() => handleFilterChange('employment', type)}
                              />
                              <span className="text-sm text-gray-700">
                                {type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Location Filter */}
                  {uniqueLocations.length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleFilterSection('location')}
                        className="flex items-center justify-between w-full mb-3"
                      >
                        <h3 className="font-medium text-gray-900">Location</h3>
                        {expandedFilterSections.includes('location') ? (
                          <ChevronUp className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        )}
                      </button>
                      {expandedFilterSections.includes('location') && (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {uniqueLocations.sort().map((location) => (
                            <label
                              key={location}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                            >
                              <Checkbox
                                checked={selectedLocations.includes(location)}
                                onCheckedChange={() => handleFilterChange('location', location)}
                              />
                              <span className="text-sm text-gray-700">{location}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}

          {/* Job Listings */}
          <div className="flex-1 min-w-0">

            {/* All Categories */}
            {filteredCategories.length === 0 ? (
              <Card className="overflow-hidden shadow-sm">
                <div className="p-8 sm:p-12 text-center">
                  <div className="max-w-2xl mx-auto">
                    {hasActiveFilters ? (
                      <>
                        <h3 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 mb-4 sm:mb-6">
                          No matching jobs found
                        </h3>
                        <p className="text-gray-600 text-sm sm:text-base mb-6">
                          We couldn't find any jobs that match your current filters. Try adjusting your search criteria or clearing the filters to see all available positions.
                        </p>
                        <button
                          onClick={resetAllFilters}
                          className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm sm:text-base transition-colors"
                        >
                          Reset all filters
                        </button>
                      </>
                    ) : (
                      <>
                        <h3 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 mb-4 sm:mb-6">
                          We're Not Hiring Right Now — But Our Mission Continues
                        </h3>
                        <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-4">
                          While there are no open roles at the moment, our journey continues.
                        </p>
                        <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                          Please check back soon — new opportunities will open as we grow together.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredCategories.map((category) => (
                  <Card key={category.categoryId} className="overflow-hidden shadow-sm">
                    <div className="flex flex-col sm:flex-row items-stretch">
                      <div className="w-full sm:w-1/4 bg-gradient-to-r from-[#2d6dad] to-[#2e83e1] p-3 sm:p-4">
                        <h3 className="font-medium text-white text-sm sm:text-base">{category.name}</h3>
                      </div>
                      <button
                        onClick={() => toggleCategory(category.categoryId)}
                        className="flex-1 flex items-center justify-between p-3 sm:p-4 bg-white hover:bg-gray-50"
                      >
                        <span className="text-gray-600 text-sm sm:text-base line-clamp-2 sm:line-clamp-1 text-left mr-2">
                          {category.description}
                        </span>
                        {expandedCategories.includes(category.categoryId) ? (
                          <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 text-gray-400" />
                        )}
                      </button>
                    </div>

                    {expandedCategories.includes(category.categoryId) && (
                      <div className="border-t">
                        {category.jobs.map((job) => (
                          <Link key={job.jobId} href={`/jobs/${job.jobId}`}>
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 hover:bg-gray-50 border-b last:border-b-0">
                              <div className="flex-1 mb-2 sm:mb-0">
                                <h4 className="font-medium text-gray-900 text-sm sm:text-base">
                                  {job.title}
                                </h4>
                              </div>
                              <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-2 sm:gap-4">
                                <span className="text-[#164a7a] text-xs sm:text-sm font-medium">
                                  {job.location}
                                </span>
                                <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
